/**
 * V2BasicConfigSection — V2-native editor section for basic step config
 *
 * Edits: Offline First, System Permission, Countdown
 * V2 paths: content.offlineFirst, content.systemPrompt, content.countdown
 *
 * Animation controls (Screen Entrance + legacy Animation Enabled) moved to
 * the dedicated V2AnimationSection — see v2AnimationSectionConfig.ts.
 *
 * Replaces: OnboardingBasicConfigSection (for V2 context)
 * Reuses: ToggleElement, SAccordion
 *
 * Key V2 differences:
 * - 'useLocalAssetsIfAvailable' → 'content.offlineFirst'
 * - 'permission.type' → 'content.systemPrompt.permissionType'
 * - 'timer.duration' → 'content.countdown.duration'
 * - 'timer' (object) → 'content.countdown' (object)
 *
 * Sprint: schema-v2-migration, Sprint 3
 */

import React, { useCallback, useMemo } from 'react';
import { Box, Checkbox, Typography, FormControl, Select, MenuItem } from '@mui/material';
import { SAccordion, StyledNumberInput } from '@modules/ui-components';
import { ToggleElement } from '../../../factories/elements/ToggleElement';
import { EditorGuideProvider } from '../../../contexts';
import {
  V2_OFFLINE_FIRST_CONFIG,
  V2_SYSTEM_PROMPT_CONFIG,
  V2_PERMISSION_TYPE_CONFIG,
  V2_PERMISSION_TYPE_OPTIONS,
  V2_COUNTDOWN_DURATION_CONFIG,
  V2_COUNTDOWN_UNIT_CONFIG,
  V2_COUNTDOWN_UNIT_OPTIONS,
} from '../../../config/v2BasicConfigConfig';
import { withScreenEditorFieldGuide } from '../../../config/screenEditorFieldGuideResolver';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM } from '../../../config/ui';
import {
  checkboxIconSx,
  checkboxCheckedIconSx,
  checkboxSx,
  mainFieldLabelEnabledSx,
  mainFieldLabelDisabledSx,
} from '../../../config/editorFieldStyles';
import { getValueByPath } from '../../../utils/v2FieldOperations';
import { getSectionTestId, getFieldTestId, getCheckboxTestId, getInputTestId } from '../../../utils/testIdUtils';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

export interface V2BasicConfigSectionProps {
  /** V2 StepNode being edited */
  step: FlowStepNode;

  /** Current editing locale */
  locale: string;

  /** Callback when a V2 path value changes */
  onChange: (path: string, value: unknown) => void;

  /** Whether section is disabled (saving state) */
  disabled?: boolean;

  /** Whether section is expanded by default */
  defaultExpanded?: boolean;
}

export const V2BasicConfigSection: React.FC<V2BasicConfigSectionProps> = ({
  step,
  locale: _locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
}) => {
  // ============================================================================
  // VALUE GETTERS
  // ============================================================================

  // System prompt (nullable object)
  const systemPrompt = useMemo(() => {
    return getValueByPath<{ permissionType: string }>(step, V2_SYSTEM_PROMPT_CONFIG.path);
  }, [step]);

  const isSystemPromptEnabled = systemPrompt !== null && systemPrompt !== undefined;
  const permissionType = systemPrompt?.permissionType ?? 'notification';

  // Countdown (nullable object)
  const countdown = useMemo(() => {
    return getValueByPath<{ duration: number; unit: string }>(step, 'content.countdown');
  }, [step]);

  const isCountdownEnabled = countdown !== null && countdown !== undefined;
  const countdownDuration = countdown?.duration ?? 5;
  const countdownUnit = countdown?.unit ?? 'seconds';

  // Boolean toggles
  const offlineFirst = useMemo(() => {
    const value = getValueByPath<boolean>(step, V2_OFFLINE_FIRST_CONFIG.path);
    return value ?? false;
  }, [step]);


  // ============================================================================
  // HANDLERS
  // ============================================================================

  // System prompt enable/disable
  const handleSystemPromptEnabledChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onChange(V2_SYSTEM_PROMPT_CONFIG.path, { permissionType: 'notification' });
      } else {
        onChange(V2_SYSTEM_PROMPT_CONFIG.path, null);
      }
    },
    [onChange]
  );

  // Permission type change
  const handlePermissionTypeChange = useCallback(
    (event: { target: { value: string } }) => {
      onChange(V2_PERMISSION_TYPE_CONFIG.path, event.target.value);
    },
    [onChange]
  );

  // Countdown enable/disable
  const handleCountdownEnabledChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onChange('content.countdown', { duration: 5, unit: 'seconds' });
      } else {
        onChange('content.countdown', null);
      }
    },
    [onChange]
  );

  // Countdown duration change
  const handleCountdownDurationChange = useCallback(
    (value: number | null) => {
      if (value !== null) {
        onChange(V2_COUNTDOWN_DURATION_CONFIG.path, value);
      }
    },
    [onChange]
  );

  // Countdown unit change
  const handleCountdownUnitChange = useCallback(
    (event: { target: { value: string } }) => {
      onChange(V2_COUNTDOWN_UNIT_CONFIG.path, event.target.value);
    },
    [onChange]
  );

  // Offline first toggle
  const handleOfflineFirstChange = useCallback(
    (value: boolean) => onChange(V2_OFFLINE_FIRST_CONFIG.path, value),
    [onChange]
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  const SECTION_NAME = 'Basic Config';

  return (
    <SAccordion
      title="Basic Config"
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      <EditorGuideProvider>
        {/* System Permission (nullable with checkbox) */}
        <Box
          data-testid={getFieldTestId(SECTION_NAME, 'Permission')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mb: FIELD_MARGIN_BOTTOM,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={isSystemPromptEnabled}
              onChange={handleSystemPromptEnabledChange}
              disabled={disabled}
              icon={<Box sx={checkboxIconSx} />}
              checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
              sx={checkboxSx}
              data-testid={getCheckboxTestId(SECTION_NAME, 'Permission')}
            />
            <Typography sx={isSystemPromptEnabled ? mainFieldLabelEnabledSx : mainFieldLabelDisabledSx}>
              {V2_SYSTEM_PROMPT_CONFIG.label}
            </Typography>
            {isSystemPromptEnabled && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={permissionType}
                  onChange={handlePermissionTypeChange}
                  disabled={disabled}
                  data-testid={getInputTestId(SECTION_NAME, 'Permission')}
                >
                  {V2_PERMISSION_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {/* Countdown (nullable with checkbox) */}
        <Box
          data-testid={getFieldTestId(SECTION_NAME, 'Countdown')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mb: FIELD_MARGIN_BOTTOM,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={isCountdownEnabled}
              onChange={handleCountdownEnabledChange}
              disabled={disabled}
              icon={<Box sx={checkboxIconSx} />}
              checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
              sx={checkboxSx}
              data-testid={getCheckboxTestId(SECTION_NAME, 'Countdown')}
            />
            <Typography sx={isCountdownEnabled ? mainFieldLabelEnabledSx : mainFieldLabelDisabledSx}>
              Countdown
            </Typography>
            {isCountdownEnabled && (
              <>
                <StyledNumberInput
                  value={countdownDuration}
                  onChange={handleCountdownDurationChange}
                  min={V2_COUNTDOWN_DURATION_CONFIG.min}
                  max={V2_COUNTDOWN_DURATION_CONFIG.max}
                  disabled={disabled}
                  data-testid={getInputTestId(SECTION_NAME, 'Countdown')}
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={countdownUnit}
                    onChange={handleCountdownUnitChange}
                    disabled={disabled}
                    data-testid={getInputTestId(SECTION_NAME, 'Countdown Unit')}
                  >
                    {V2_COUNTDOWN_UNIT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </Box>

        {/* Offline First toggle */}
        <ToggleElement
          fieldConfig={withScreenEditorFieldGuide(V2_OFFLINE_FIRST_CONFIG, 'basicConfig')}
          value={offlineFirst}
          onChange={handleOfflineFirstChange}
          disabled={disabled}
          data-testid={getFieldTestId(SECTION_NAME, V2_OFFLINE_FIRST_CONFIG.label)}
        />
      </EditorGuideProvider>
    </SAccordion>
  );
};
